import dynamic from "next/dynamic";
import { memo, useCallback } from "react";

import EmptyPage from "@components/cms/EmptyPage/EmptyPage";
import LoadingPage from "@components/cms/LoadingPage/LoadingPage";
import Block from "@components/cmsBlocks/Block";
import { getBlockTemplate } from "@components/cmsBlocks/getBlockTemplate";
import { useStore } from "@hooks/useZustandProvider";

import GetWidget from "./components/GetWidget/GetWidget";

const UploadImages = dynamic(
  () => import("@modals/cms/UploadImages/UploadImages"),
  {
    ssr: false,
  },
);

const UploadImage = dynamic(
  () => import("@modals/cms/UploadImage/UploadImage"),
  {
    ssr: false,
  },
);

const EditCollection = dynamic(
  () => import("@modals/cms/EditCollection/EditCollection"),
  {
    ssr: false,
  },
);

const SetttingShowMore = dynamic(
  () => import("@modals/cms/SetttingShowMore/SetttingShowMore"),
  {
    ssr: false,
  },
);

const RemoveWidgetNotice = dynamic(
  () => import("@modals/cms/RemoveWidgetNotice/RemoveWidgetNotice"),
  {
    ssr: false,
  },
);

const Page = ({ blocks = [], loading }) => {
  const {
    cms,
    modals,
    updateModals,
    updateDataBlock,
    updateDataWidget,
    toggleSettings,
    toggleActiveSeo,
  } = useStore();

  const { isEditMode } = cms.uiOptions;
  const { isWidgetEditing, draft } = cms.widget;
  const { type, id } = draft;
  const { blockIndex, collectionType, lang, isPageNotFound } = cms.page;

  const openPageStrucrute = () => {
    toggleSettings();
    toggleActiveSeo(false);
  };

  const {
    isOpenUploadImagesModal,
    isOpenUploadImageModal,
    isOpenEditSliderModal,
    isOpenEditCollectionModal,
    isOpenCampaignRulesModal,
    isOpenSetttingShowMoreModal,
    isOpenRemoveWidgetNoticeModal,
  } = modals;

  const toggleUploadImagesModal = () => {
    updateModals({
      isOpenUploadImagesModal: !isOpenUploadImagesModal,
    });
  };

  const toggleUploadImageModal = () => {
    updateModals({
      isOpenUploadImageModal: !isOpenUploadImageModal,
    });
  };

  const toggleEditCollectionModal = () => {
    updateModals({
      isOpenEditCollectionModal: !isOpenEditCollectionModal,
    });
  };

  const toggleSetttingShowMoreModal = () => {
    updateModals({
      isOpenSetttingShowMoreModal: !isOpenSetttingShowMoreModal,
    });
  };

  const toggleRemoveWidgetNoticeModal = () => {
    updateModals({
      isOpenRemoveWidgetNoticeModal: !isOpenRemoveWidgetNoticeModal,
    });
  };

  const toggleCampaignRulesModal = () => {
    updateModals({
      isOpenCampaignRulesModal: !isOpenCampaignRulesModal,
    });
  };

  const updateField = useCallback(
    (payload) => {
      isWidgetEditing
        ? updateDataWidget(payload)
        : updateDataBlock(payload, blockIndex);
    },
    [blockIndex, isWidgetEditing],
  );

  // const newBlocks = blocks.map((block) => {
  //   if (block.widgetId) {
  //     return block;
  //   }

  //   return {
  //     ...block,
  //     // settings: mergeBlockSettingsWithDefault(block),
  //   }
  // })

  return isEditMode ? (
    typeof loading !== "boolean" || loading ? (
      <LoadingPage openPageStrucrute={openPageStrucrute} />
    ) : blocks?.length ? (
      <>
        {isOpenUploadImageModal && (
          <UploadImage
            blockIndex={blockIndex}
            updateField={updateField}
            isOpen={isOpenUploadImageModal}
            toggleModal={toggleUploadImageModal}
          />
        )}
        {isOpenUploadImagesModal && (
          <UploadImages
            blockIndex={blockIndex}
            updateField={updateField}
            isOpen={isOpenUploadImagesModal}
            toggleModal={toggleUploadImagesModal}
          />
        )}
        {isOpenEditCollectionModal && (
          <EditCollection
            collectionType={collectionType}
            updateField={updateField}
            isOpen={isOpenEditCollectionModal}
            toggleModal={toggleEditCollectionModal}
          />
        )}
        {isOpenSetttingShowMoreModal && (
          <SetttingShowMore
            blockIndex={blockIndex}
            updateField={updateField}
            isOpen={isOpenSetttingShowMoreModal}
            toggleModal={toggleSetttingShowMoreModal}
          />
        )}

        {isOpenRemoveWidgetNoticeModal && (
          <RemoveWidgetNotice
            updateField={updateField}
            isOpen={isOpenRemoveWidgetNoticeModal}
            toggleModal={toggleRemoveWidgetNoticeModal}
          />
        )}

        {isWidgetEditing ? (
          <GetWidget type={type} id={id} updateField={updateField} />
        ) : (
          blocks.map((block, key) => {
            if (!block) {
              return null;
            }

            const { name, isShow, widgetId, widgetType } = block;

            const blockTemplate = getBlockTemplate(
              widgetId ? widgetType : name,
            );

            if (widgetId) {
              return (
                <GetWidget
                  key={`admin_${lang}_${key}`}
                  isShow={isShow}
                  type={widgetType}
                  id={widgetId}
                />
              );
            }

            const { Component } = blockTemplate;

            if (!Component) return null;

            return isShow ? (
              <Block
                index={key}
                key={`admin_${lang}_${key}`}
                block={block}
                blockTemplate={blockTemplate}
                updateField={updateField}
                {...{ "data-block-index": key }}
                {...{ "data-is-widget": false }}
              >
                <Component />
              </Block>
            ) : null;
          })
        )}
      </>
    ) : (
      <EmptyPage openPageStrucrute={openPageStrucrute} />
    )
  ) : (
    blocks.map((block, key) => {
      const { name, isShow, widgetId, widgetType } = block;

      const blockTemplate = getBlockTemplate(widgetId ? widgetType : name);

      if (widgetId) {
        return (
          <GetWidget
            key={`admin_${lang}_${key}`}
            isShow={isShow}
            type={widgetType}
            id={widgetId}
          />
        );
      }

      const { Component } = blockTemplate;

      if (!Component) return null;

      return isShow ? (
        <Block
          index={key}
          key={`${lang}_${key}`}
          block={block}
          blockTemplate={blockTemplate}
        >
          <Component />
        </Block>
      ) : null;
    })
  );
};

export default memo(Page);
